import { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import { Anchor, MapPin, FileText } from 'lucide-react';
import { ZipAutoFillInput } from './ZipAutoFillInput';
import api from '../../../lib/api';
import { quoteJobService } from '../../../services/quoteJobService';
import { useLeadSources } from '../../../Hooks/useLeadSources';
import toast from 'react-hot-toast';

export default function UpdateQuoteModal({ quote, isOpen, onClose, onSuccess }) {
    const { leadSources = [], isLoading: loadingLeadSources } = useLeadSources();
    const [services, setServices] = useState([]);
    const [loadingServices, setLoadingServices] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const [formData, setFormData] = useState({
        insurance_company_email: '',
        boat_type: '',
        boat_year: '',
        boat_make: '',
        boat_length: '',
        boat_location: '',
        zip_code: '',
        message: '',
        service_id: '',
        lead_source_id: '',
        is_boat_in_water: true,
        dock: '',
        slip_number: '',
    });

    useEffect(() => {
        if (isOpen && quote) {
            // Load services
            setLoadingServices(true);
            quoteJobService.getServices()
                .then((response) => {
                    if (response.data.success) {
                        setServices(response.data.data || []);
                    }
                })
                .catch(() => {
                    console.log('Services not available');
                })
                .finally(() => {
                    setLoadingServices(false);
                });

            // Initialize form with quote data
            setFormData({
                insurance_company_email: quote.insurance_company_email || '',
                boat_type: quote.boat_type || '',
                boat_year: quote.boat_year || '',
                boat_make: quote.boat_make || '',
                boat_length: quote.boat_length || '',
                boat_location: quote.boat_location || '',
                zip_code: quote.zip_code || '',
                message: quote.message || '',
                service_id: quote.service_id || quote.service?.id || '',
                lead_source_id: quote.lead_source_id || quote.lead_source?.id || '',
                is_boat_in_water: quote.is_boat_in_water ?? true,
                dock: quote.dock || '',
                slip_number: quote.slip_number || '',
            });
        }
    }, [isOpen, quote]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!quote?.id) return;

        setIsSubmitting(true);
        try {
            const payload = {
                insurance_company_email: formData.insurance_company_email || null,
                boat_type: formData.boat_type || null,
                boat_year: formData.boat_year || null,
                boat_make: formData.boat_make || null,
                boat_length: formData.boat_length || null,
                boat_location: formData.boat_location || null,
                zip_code: formData.zip_code || null,
                message: formData.message || null,
                service_id: formData.service_id ? Number(formData.service_id) : null,
                lead_source_id: formData.lead_source_id ? Number(formData.lead_source_id) : null,
                is_boat_in_water: formData.is_boat_in_water,
                dock: formData.dock || null,
                slip_number: formData.slip_number || null,
            };

            const response = await api.put(`/company/quotes/${quote.id}`, payload);
            
            if (response.data.success) {
                toast.success('Quote updated successfully');
                onSuccess?.();
                onClose();
            } else {
                throw new Error(response.data.message || 'Failed to update quote');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || error.message || 'Failed to update quote');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div
                className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                    <h2 className="text-xl font-bold text-gray-900">Update Quote</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition"
                        aria-label="Close"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6">
                    <div className="space-y-6">
                        {/* Insurance Company Email */}
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                Insurance Company Email
                            </label>
                            <input
                                type="email"
                                placeholder="e.g., info@insurance.com"
                                value={formData.insurance_company_email}
                                onChange={(e) => setFormData(prev => ({ ...prev, insurance_company_email: e.target.value }))}
                                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                            />
                        </div>

                        {/* Vessel Information */}
                        <div>
                            <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-1.5">
                                <Anchor className="w-4 h-4" />
                                Vessel Information
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Vessel Type
                                    </label>
                                    <select
                                        value={formData.boat_type}
                                        onChange={(e) => setFormData(prev => ({ ...prev, boat_type: e.target.value }))}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                    >
                                        <option value="">Select vessel type</option>
                                        <option value="Power Boat">Power Boat</option>
                                        <option value="Sailboat">Sailboat</option>
                                        <option value="Yacht">Yacht</option>
                                        <option value="Catamaran">Catamaran</option>
                                        <option value="Fishing Boat">Fishing Boat</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Vessel Year
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="e.g., 2020"
                                        value={formData.boat_year}
                                        onChange={(e) => setFormData(prev => ({ ...prev, boat_year: e.target.value }))}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Vessel Manufacturer
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="e.g., Beneteau"
                                        value={formData.boat_make}
                                        onChange={(e) => setFormData(prev => ({ ...prev, boat_make: e.target.value }))}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Vessel Length
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="e.g., 45 feet"
                                        value={formData.boat_length}
                                        onChange={(e) => setFormData(prev => ({ ...prev, boat_length: e.target.value }))}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                    />
                                </div>
                            </div>
                        </div>

                        <hr className="border-gray-200" />

                        {/* Location Details */}
                        <div>
                            <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-1.5">
                                <MapPin className="w-4 h-4" />
                                Location Details
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Zip Code
                                    </label>
                                    <ZipAutoFillInput
                                        zip={formData.zip_code}
                                        onZipChange={(zip) => setFormData(prev => ({ ...prev, zip_code: zip || '' }))}
                                        onAddressResolved={({ city, state, country }) => {
                                            const parts = [city, state, country].filter(Boolean);
                                            if (parts.length > 0) {
                                                setFormData(prev => ({ ...prev, boat_location: parts.join(', ') }));
                                            }
                                        }}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Vessel Location
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="e.g., Miami Marina"
                                        value={formData.boat_location}
                                        onChange={(e) => setFormData(prev => ({ ...prev, boat_location: e.target.value }))}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="flex items-center space-x-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={formData.is_boat_in_water}
                                            onChange={(e) => setFormData(prev => ({ ...prev, is_boat_in_water: e.target.checked }))}
                                            className="w-4 h-4 text-blue-500 rounded focus:ring-blue-500"
                                        />
                                        <span className="text-xs font-medium text-gray-700">Is boat in water?</span>
                                    </label>
                                </div>

                                {formData.is_boat_in_water && (
                                    <>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                                Dock
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="e.g., A"
                                                value={formData.dock}
                                                onChange={(e) => setFormData(prev => ({ ...prev, dock: e.target.value }))}
                                                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                                Slip Number
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="e.g., 12"
                                                value={formData.slip_number}
                                                onChange={(e) => setFormData(prev => ({ ...prev, slip_number: e.target.value }))}
                                                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        <hr className="border-gray-200" />

                        {/* Service Information */}
                        <div>
                            <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-1.5">
                                <FileText className="w-4 h-4" />
                                Service Information
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Survey Type
                                    </label>
                                    <select
                                        value={formData.service_id}
                                        onChange={(e) => setFormData(prev => ({ ...prev, service_id: e.target.value }))}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                        disabled={loadingServices || !services || services.length === 0}
                                    >
                                        <option value="">
                                            {loadingServices ? 'Loading services...' : 'Select service'}
                                        </option>
                                        {services && services.map(service => (
                                            <option key={service.id} value={service.id}>{service.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Lead Source
                                    </label>
                                    <select
                                        value={formData.lead_source_id}
                                        onChange={(e) => setFormData(prev => ({ ...prev, lead_source_id: e.target.value }))}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs"
                                        disabled={loadingLeadSources}
                                    >
                                        <option value="">
                                            {loadingLeadSources ? 'Loading lead sources...' : 'Select Lead Source'}
                                        </option>
                                        {leadSources.map(source => (
                                            <option key={source.id} value={source.id}>{source.name}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-xs font-medium text-gray-700 mb-1.5">
                                        Message / Notes
                                    </label>
                                    <textarea
                                        placeholder="Add any additional information or special requirements..."
                                        value={formData.message}
                                        onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                                        rows={4}
                                        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500/20 text-xs resize-none"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="mt-6 flex items-center justify-end gap-3 pt-6 border-t border-gray-200">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 hover:bg-gray-50 transition"
                            disabled={isSubmitting}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700 transition flex items-center gap-2 disabled:opacity-70"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Updating...
                                </>
                            ) : (
                                'Update Quote'
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
